/**
 * مرکزِ کنترل — فقط ادمینِ اصلی (v2.142.0).
 *
 * کاربر (۱۴۰۵/۰۷/۰۷): «فقط برای ادمینِ اصلی: تمامِ رفتارهای لحظه‌ای را رصد کنم — کجاست، چه زده، چه ادیت زده، با نمودار و
 * ظاهرِ خوب؛ از هاست هم: چقدر سی‌پی‌یو و رم مصرف می‌شود — یک کنترل سنتر». هر ۵ ثانیه تازه می‌شود.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Badge, Button, Card, Col, Empty, Progress, Result, Row, Segmented, Select, Space, Statistic, Table,
         Tag, Tooltip, Typography } from "antd";
import { ClockCircleOutlined, CloudServerOutlined, DatabaseOutlined, EditOutlined, EyeOutlined,
         PauseCircleOutlined, PlayCircleOutlined, SelectOutlined, TeamOutlined } from "@ant-design/icons";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";

const { Text, Title } = Typography;
const C_ACT = "#4F46E5";     // رفتار (صفحه/کلیک)
const C_EDIT = "#F59E0B";    // ویرایش/ثبت
const fa = (n) => (n == null ? "—" : Number(n).toLocaleString("fa-IR"));
const bytes = (b) => {
  if (b == null) return "—";
  const u = ["بایت", "KB", "MB", "GB", "TB"]; let i = 0; let v = Number(b);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toLocaleString("fa-IR", { maximumFractionDigits: 1 })} ${u[i]}`;
};
const ago = (s) => (s < 60 ? `${fa(s)} ثانیه پیش` : s < 3600 ? `${fa(Math.floor(s / 60))} دقیقه پیش`
  : s < 86400 ? `${fa(Math.floor(s / 3600))} ساعت پیش` : `${fa(Math.floor(s / 86400))} روز پیش`);
const hms = (iso) => (iso ? new Date(iso).toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "");
const uptime = (s) => (s == null ? "—" : `${fa(Math.floor(s / 86400))} روز و ${fa(Math.floor((s % 86400) / 3600))} ساعت`);
const gaugeColor = (p) => (p == null ? "#bbb" : p >= 85 ? "#DC2626" : p >= 65 ? "#F59E0B" : "#16A34A");

/** نمودارِ ستونیِ ساعتی — دو سری کنارِ هم (رفتار / ویرایش). SVG خالص، بدونِ کتابخانه. */
function HourlyChart({ data }) {
  const W = 760; const H = 190; const P = { l: 34, r: 8, t: 10, b: 28 };
  const max = Math.max(1, ...data.map((d) => Math.max(d.activity, d.edits)));
  const bw = (W - P.l - P.r) / Math.max(data.length, 1);
  const y = (v) => H - P.b - ((H - P.t - P.b) * v) / max;
  const ticks = [0, Math.ceil(max / 2), max];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 210, direction: "ltr" }} role="img"
      aria-label="فعالیتِ ساعت‌به‌ساعت">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={P.l} x2={W - P.r} y1={y(t)} y2={y(t)} stroke="currentColor" strokeOpacity=".12" />
          <text x={P.l - 6} y={y(t) + 4} fontSize="10" textAnchor="end" fill="currentColor" opacity=".6">{fa(t)}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const x = P.l + i * bw; const w = Math.max(2, bw / 2 - 2);
        return (
          <g key={`${d.day}-${d.hour}`}>
            <title>{`${d.day} ساعت ${d.hour} — رفتار: ${d.activity} · ویرایش: ${d.edits}`}</title>
            <rect x={x + 1} y={y(d.activity)} width={w} height={H - P.b - y(d.activity)} rx="2" fill={C_ACT} />
            <rect x={x + 1 + w + 1} y={y(d.edits)} width={w} height={H - P.b - y(d.edits)} rx="2" fill={C_EDIT} />
            {(data.length <= 24 || i % Math.ceil(data.length / 24) === 0) && (
              <text x={x + bw / 2} y={H - 10} fontSize="10" textAnchor="middle" fill="currentColor" opacity=".6">
                {fa(Number(d.hour))}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** خطِ کوچکِ تاریخچهٔ CPU/RAM در همین نشست. */
function Spark({ values, color }) {
  if (values.length < 2) return <div style={{ height: 28 }} />;
  const W = 140; const H = 28;
  const pts = values.map((v, i) => `${(i * W) / (values.length - 1)},${H - (H * Math.min(v, 100)) / 100}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, direction: "ltr" }} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function Gauge({ title, percent, sub, history, icon }) {
  const col = gaugeColor(percent);
  return (
    <Card size="small" style={{ height: "100%" }}>
      <Space direction="vertical" size={2} style={{ width: "100%", alignItems: "center" }}>
        <Text type="secondary">{icon} {title}</Text>
        <Progress type="dashboard" percent={percent ?? 0} size={112} strokeColor={col}
          format={(p) => <span style={{ fontSize: 20, fontWeight: 700 }}>{percent == null ? "—" : `${fa(p)}٪`}</span>} />
        <Text style={{ fontSize: 12 }}>{sub}</Text>
        {history && <Spark values={history} color={col} />}
      </Space>
    </Card>
  );
}

const TYPE = {
  page: { color: "blue", icon: <EyeOutlined />, label: "صفحه" },
  click: { color: "purple", icon: <SelectOutlined />, label: "کلیک" },
  edit: { color: "orange", icon: <EditOutlined />, label: "ثبت/ویرایش" },
};

export default function ControlCenterPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [who, setWho] = useState(null);
  const [hours, setHours] = useState(24);
  const [live, setLive] = useState(true);
  const [types, setTypes] = useState(["page", "click", "edit"]);
  const hist = useRef({ cpu: [], mem: [] });

  const load = useCallback(() => {
    api.get("/api/v1/activity/control-center", { params: { username: who || undefined, hours } })
      .then((r) => {
        const h = r.data?.host || {};
        if (h.cpu_percent != null) hist.current.cpu = [...hist.current.cpu, h.cpu_percent].slice(-60);
        if (h.mem_percent != null) hist.current.mem = [...hist.current.mem, h.mem_percent].slice(-60);
        setData(r.data); setErr(null);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "خطا در دریافت"));
  }, [who, hours]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 5000);
    return () => clearInterval(t);
  }, [live, load]);

  const feed = useMemo(() => (data?.feed || []).filter((f) => types.includes(f.type)), [data, types]);
  if (user?.role !== "super_admin") {
    return <Result status="403" title="فقط ادمینِ اصلی" subTitle="مرکزِ کنترل فقط برای ادمینِ اصلی است." />;
  }
  const h = data?.host || {};
  const userOpts = (data?.users || []).map((u) => ({ value: u.username, label: u.full_name }));
  const maxUser = Math.max(1, ...(data?.per_user_today || []).map((u) => u.pages + u.clicks + u.edits));
  const maxPage = Math.max(1, ...(data?.top_pages || []).map((p) => p.c));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <Space size={10} align="center">
          <Title level={4} style={{ margin: 0 }}>مرکز کنترل</Title>
          <Badge status={live ? "processing" : "default"} text={live ? "زنده — هر ۵ ثانیه" : "متوقف"} />
          {err && <Tag color="red">{err}</Tag>}
        </Space>
        <Space wrap>
          <Select allowClear showSearch optionFilterProp="label" placeholder="همهٔ کاربران" style={{ width: 180 }}
            value={who} onChange={setWho} options={userOpts} />
          <Segmented value={hours} onChange={setHours}
            options={[{ value: 6, label: "۶ ساعت" }, { value: 24, label: "۲۴ ساعت" }, { value: 168, label: "۷ روز" }]} />
          <Tooltip title={live ? "توقفِ تازه‌سازی" : "ادامهٔ تازه‌سازی"}>
            <Button icon={live ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={() => setLive((v) => !v)}
              aria-label="زنده" />
          </Tooltip>
        </Space>
      </div>

      {/* ── سرور ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Gauge title="پردازنده (CPU)" icon={<CloudServerOutlined />} percent={h.cpu_percent}
            sub={`${fa(h.cpu_count)} هسته · بار ${h.load ? h.load.map((x) => fa(x)).join(" / ") : "—"}`} history={hist.current.cpu} />
        </Col>
        <Col xs={12} md={6}>
          <Gauge title="حافظه (RAM)" icon={<CloudServerOutlined />} percent={h.mem_percent}
            sub={`${bytes(h.mem_used)} از ${bytes(h.mem_total)}`} history={hist.current.mem} />
        </Col>
        <Col xs={12} md={6}>
          <Gauge title="دیسک" icon={<DatabaseOutlined />} percent={h.disk_percent}
            sub={`${bytes(h.disk_used)} از ${bytes(h.disk_total)}`} />
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ height: "100%" }}>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Statistic title="آنلاین الان" value={data?.online_count ?? 0} prefix={<TeamOutlined />}
                valueStyle={{ color: "#16A34A" }} formatter={(v) => fa(v)} />
              <Text type="secondary" style={{ fontSize: 12 }}><ClockCircleOutlined /> روشن از {uptime(h.uptime_seconds)}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}><DatabaseOutlined /> دیتابیس {bytes(h.db_size)} · {fa(h.db_connections)} اتصال</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>حافظهٔ برنامه {bytes(h.app_rss)}{h.swap_total ? ` · swap ${bytes(h.swap_used)}` : ""}</Text>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* ── کاربران ── */}
      <Card size="small" title="کاربران — کجا هستند، آخرین کار" style={{ marginBottom: 12 }}>
        {!(data?.users || []).length ? <Empty description="هنوز فعالیتی ثبت نشده" /> : (
          <Row gutter={[10, 10]}>
            {data.users.map((u) => (
              <Col key={u.username} xs={24} sm={12} lg={8} xl={6}>
                <Card size="small" hoverable onClick={() => setWho(who === u.username ? null : u.username)}
                  style={{ borderColor: who === u.username ? C_ACT : undefined, opacity: u.online ? 1 : 0.7 }}>
                  <Space align="start">
                    <Badge dot status={u.online ? "success" : "default"} offset={[-4, 30]}>
                      <Avatar style={{ background: u.online ? C_ACT : "#9CA3AF" }}>{(u.full_name || "?").slice(0, 1)}</Avatar>
                    </Badge>
                    <div style={{ minWidth: 0 }}>
                      <b>{u.full_name}</b>
                      <div style={{ fontSize: 12 }}>
                        {u.online ? <Text type="success">آنلاین</Text> : <Text type="secondary">{ago(u.seconds_ago)}</Text>}
                      </div>
                      <div style={{ fontSize: 12 }}><EyeOutlined /> {u.page || u.path || "—"}</div>
                      {u.last_action && <div style={{ fontSize: 12, color: "#6B7280" }}><SelectOutlined /> {u.last_action}</div>}
                    </div>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* ── نمودارِ ساعتی ── */}
      <Card size="small" style={{ marginBottom: 12 }}
        title={<Space>فعالیتِ ساعت‌به‌ساعت{who ? <Tag color="blue">{userOpts.find((o) => o.value === who)?.label}</Tag> : null}</Space>}
        extra={<Space size={12}>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: C_ACT, borderRadius: 2 }} /> صفحه و کلیک</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: C_EDIT, borderRadius: 2 }} /> ثبت و ویرایش</span>
        </Space>}>
        {data ? <HourlyChart data={data.hourly || []} /> : <div style={{ height: 210 }} />}
      </Card>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title="امروز — هر کاربر" style={{ height: "100%" }}>
            <Table size="small" rowKey="username" pagination={false} dataSource={data?.per_user_today || []}
              onRow={(r) => ({ onClick: () => setWho(r.username), style: { cursor: "pointer" } })}
              columns={[
                { title: "کاربر", dataIndex: "full_name" },
                { title: "صفحه", dataIndex: "pages", align: "center", render: fa, sorter: (a, b) => a.pages - b.pages },
                { title: "کلیک", dataIndex: "clicks", align: "center", render: fa, sorter: (a, b) => a.clicks - b.clicks },
                { title: "ثبت/ویرایش", dataIndex: "edits", align: "center", render: fa, sorter: (a, b) => a.edits - b.edits },
                { title: "", width: 120, render: (_, r) => {
                  const t = r.pages + r.clicks + r.edits;
                  return (
                    <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: "rgba(0,0,0,.06)", direction: "ltr" }}>
                      <div style={{ width: `${(100 * (r.pages + r.clicks)) / maxUser}%`, background: C_ACT }} />
                      <div style={{ width: `${(100 * r.edits) / maxUser}%`, background: C_EDIT }} />
                      <span style={{ display: "none" }}>{t}</span>
                    </div>);
                } },
              ]} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="صفحه‌های پربازدیدِ امروز" style={{ height: "100%" }}>
            {!(data?.top_pages || []).length ? <Empty /> : data.top_pages.map((p) => (
              <div key={p.page} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span>{p.page}</span><Text type="secondary">{fa(p.c)} بار · {fa(p.users)} نفر</Text>
                </div>
                <Progress percent={(100 * p.c) / maxPage} showInfo={false} strokeColor={C_ACT} size="small" />
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      {/* ── جریانِ زنده ── */}
      <Card size="small" title="جریانِ لحظه‌ای — چه کسی، چه کاری"
        extra={<Space size={4} wrap>
          {Object.entries(TYPE).map(([k, t]) => (
            <Tag.CheckableTag key={k} checked={types.includes(k)}
              onChange={(c) => setTypes((prev) => (c ? [...prev, k] : prev.filter((x) => x !== k)))}>
              {t.icon} {t.label}
            </Tag.CheckableTag>
          ))}
        </Space>}>
        <div style={{ maxHeight: 520, overflowY: "auto" }}>
          {!feed.length ? <Empty /> : feed.map((f, i) => (
            <div key={`${f.at}-${i}`} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 2px",
              borderBottom: "1px solid rgba(0,0,0,.05)" }}>
              <Text type="secondary" style={{ fontSize: 12, minWidth: 64, fontVariantNumeric: "tabular-nums" }}>{hms(f.at)}</Text>
              <Tag color={TYPE[f.type]?.color} style={{ margin: 0 }}>{TYPE[f.type]?.icon} {TYPE[f.type]?.label}</Tag>
              <a onClick={() => setWho(f.username)} style={{ minWidth: 90 }}>{f.full_name}</a>
              <span style={{ fontSize: 13, wordBreak: "break-word" }}>{f.text}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
